import * as z from 'zod'
import i18n from '@/scripts/app-configs/i18n-config'
import customValidatorMessages from '@/scripts/app-configs/vuelidate-custom-validator-messages'
import { VALIDATION_TYPES } from '@/utils/zod/zod-keys'

/**
 * Converts a Zod schema into a vee-validate typed schema with translated error messages.
 * Reads .describe({ type }) metadata from the schema (same as zod-to-vuelidate.js).
 * Resolves messages via customValidatorMessages → i18n.t(), matching vuelidate-error-extractor behavior.
 */

interface ValidationError {
  errors: string[]
  path: string
}

type ZodDescription = string | { type: string; params?: Record<string, unknown> }

interface FieldMetadata {
  refineDescriptions: ZodDescription[]
  stringDescription: ZodDescription | null
}

interface VeeValidateTypedSchema {
  __type: 'VVTypedSchema'
  parse: (value: unknown) => Promise<{ value?: unknown; errors: ValidationError[] }>
  cast: (values: unknown) => unknown
  describe: (path?: string) => { required: boolean; exists: boolean }
}

const ISSUE_TYPES = Object.freeze({
  STRING: 'string',
  NUMBER: 'number',
  ARRAY: 'array',
} as const)

function resolveMessage(validationType: string, params: Record<string, unknown> = {}): string {
  const key = (customValidatorMessages as Record<string, string>)[validationType]

  return key ? i18n.t(key, params) : i18n.t(`VALIDATIONS.${validationType}`, params)
}

function unwrapEffects(schema: z.ZodTypeAny): z.ZodTypeAny {
  let current = schema

  while (current instanceof z.ZodEffects) {
    current = current._def.schema
  }

  return current
}

function buildFieldMetadata(schema: z.ZodTypeAny): Record<string, FieldMetadata> {
  const baseSchema = unwrapEffects(schema)

  if (!(baseSchema instanceof z.ZodObject)) return {}

  return Object.fromEntries(
    Object.entries(baseSchema.shape).map(([key, fieldSchema]) => {
      const refineDescriptions: ZodDescription[] = []
      let currentSchema = fieldSchema as z.ZodTypeAny

      // Unwrap optional/default first (they wrap the effects when .optional() is at the end)
      if (currentSchema instanceof z.ZodOptional || currentSchema instanceof z.ZodDefault) {
        currentSchema = currentSchema._def.innerType
      }

      while (currentSchema instanceof z.ZodEffects) {
        const desc = currentSchema._def.description as ZodDescription | undefined

        if (desc) {
          // Description can be string or object with type property
          if (typeof desc === 'string' || (typeof desc === 'object' && desc.type)) {
            refineDescriptions.push(desc)
          }
        }
        currentSchema = currentSchema._def.schema
      }

      let stringDescription: ZodDescription | null = null

      if (currentSchema instanceof z.ZodString) {
        const desc = currentSchema._def.description as ZodDescription | undefined

        if (desc && (typeof desc === 'string' || (typeof desc === 'object' && desc.type))) {
          stringDescription = desc
        }
      }

      return [key, { refineDescriptions, stringDescription }]
    }),
  )
}

function resolveBuiltInMessage(issue: z.ZodIssue): string {
  if (issue.code === z.ZodIssueCode.invalid_type) {
    return resolveMessage(VALIDATION_TYPES.required)
  }

  if (issue.code === z.ZodIssueCode.too_small) {
    if (issue.type === ISSUE_TYPES.STRING) {
      return issue.minimum === 1
        ? resolveMessage(VALIDATION_TYPES.required)
        : resolveMessage(VALIDATION_TYPES.minLength, { min: issue.minimum })
    }

    if (issue.type === ISSUE_TYPES.NUMBER) {
      return resolveMessage('minValue', { min: issue.minimum })
    }

    if (issue.type === ISSUE_TYPES.ARRAY) {
      return resolveMessage(VALIDATION_TYPES.required)
    }
  }

  if (issue.code === z.ZodIssueCode.too_big) {
    const validation = issue.type === ISSUE_TYPES.NUMBER ? 'maxValue' : 'maxLength'

    return resolveMessage(VALIDATION_TYPES[validation], { max: issue.maximum })
  }

  return issue.message
}

function resolveIssueMessage(issue: z.ZodIssue, fieldMeta?: FieldMetadata): string {
  if (issue.code === z.ZodIssueCode.custom) {
    const desc = fieldMeta?.refineDescriptions?.[0]

    if (!desc) return resolveMessage('generic')

    if (typeof desc === 'string') {
      return resolveMessage(desc)
    }

    return resolveMessage(desc.type, desc.params || {})
  }

  if (issue.code === z.ZodIssueCode.invalid_string && issue.validation === 'regex') {
    const desc = fieldMeta?.stringDescription

    if (!desc) return resolveMessage(VALIDATION_TYPES.invalidCharacter)

    if (typeof desc === 'string') {
      return resolveMessage(desc)
    }

    return resolveMessage(desc.type)
  }

  return resolveBuiltInMessage(issue)
}

function normalizeFormPath(path: string): string {
  const pathArr = path.split('.')

  if (!pathArr.length) return ''

  return pathArr.reduce((full, segment, i) =>
    i === 0 ? segment : full + (Number(segment) >= 0 ? `[${segment}]` : `.${segment}`),
  '')
}

function processIssues(
  issues: z.ZodIssue[],
  fieldMetadata: Record<string, FieldMetadata>,
): Record<string, ValidationError> {
  const errors: Record<string, ValidationError> = {}

  issues.forEach((issue) => {
    if (issue.code === z.ZodIssueCode.invalid_union) {
      // Collect and merge recursive union error results
      const unionErrors = processIssues(
        issue.unionErrors.flatMap((ue) => ue.issues),
        fieldMetadata,
      )

      Object.assign(errors, unionErrors)
    }

    const path = normalizeFormPath(issue.path.join('.'))

    if (!path && issue.code === z.ZodIssueCode.invalid_union) return

    if (!errors[path]) {
      errors[path] = { errors: [], path }
    }

    errors[path].errors.push(resolveIssueMessage(issue, fieldMetadata[issue.path[0] as string]))
  })

  return errors
}

function getSchemaForPath(path: string | undefined, schema: z.ZodTypeAny): z.ZodTypeAny | null {
  let current: z.ZodTypeAny | null = unwrapEffects(schema)

  if (!(current instanceof z.ZodObject)) return null

  for (const p of (path || '').split(/\.|\[(\d+)\]/).filter(Boolean)) {
    if (!current) return null
    current = unwrapEffects(current)

    if (current instanceof z.ZodObject) {
      current = current.shape[p] || null
    } else if (Number(p) >= 0 && current._def?.typeName === z.ZodFirstPartyTypeKind.ZodArray) {
      current = current._def.type
    } else {
      return null
    }
  }

  return current
}

export default function zodToVeeValidateTypedSchema(zodSchema: z.ZodTypeAny): VeeValidateTypedSchema {
  const fieldMetadata = buildFieldMetadata(zodSchema)

  return {
    __type: 'VVTypedSchema',

    async parse(value: unknown) {
      const result = await zodSchema.safeParseAsync(value)

      if (result.success) {
        return { value: result.data, errors: [] }
      }

      return { errors: Object.values(processIssues(result.error.issues, fieldMetadata)) }
    },

    cast(values: unknown) {
      try {
        return zodSchema.parse(values)
      } catch {
        return values
      }
    },

    describe(path?: string) {
      try {
        if (!path) {
          return { required: !zodSchema.isOptional(), exists: true }
        }

        const field = getSchemaForPath(path, zodSchema)

        return field ? { required: !field.isOptional(), exists: true } : { required: false, exists: false }
      } catch {
        return { required: false, exists: false }
      }
    },
  }
}
